import { useEffect, useState } from 'react';
import { api, type PayOnlyRow } from '../lib/api';
import { money } from './DataTable';
import Modal from './Modal';
import { canScreen, useMe } from '../lib/me';

/**
 * 지급만(청구 없음) 예외 접수(2026-10-28 way) — 미수금 › 미청구 위칸 버튼 → 목록 창.
 *
 * way: 「거의 없어야 하는 케이스라 슈퍼관리자가 접수번호로 따로 예외 셋팅(매월 반복이라 접수번호로 통제)」.
 * - 걸린 접수는 **그 달 청구액이 0** 이면 청구 없이 기사 명세에 운행료·영업정산이 들어간다(서버 `salaryGen.ts PAY_ONLY_OK`).
 *   청구가 생기면 「청구 있음」 — 보통처럼 청구 요청을 거쳐야 한다.
 * - 넣기·고치기·끄기 = 관리자만(서버 `saveRecvPayOnly` · 시작월 = 기사 마감 안 된 달 · 고치면 옛 줄을 끄고 새 줄). 보기 = 회계 조회.
 * - 걸린 접수는 미청구 목록에서 빠진다(여기서 본다).
 */
export default function PayOnlyPanel({ mon, onChanged }: { mon: string; onChanged?: () => void }) {
  const { me } = useMe();
  const isAdmin = me?.role === 'admin';
  const canView = canScreen(me, 'accounting', 'read');
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<PayOnlyRow[] | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ recvId: '', fromMon: '', toMon: '', reason: '', allowBilled: false });

  const load = async () => {
    if (!mon) return;
    try { setRows((await api.listRecvPayOnly({ mon })).rows); setErr(''); } catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { if (canView) void load(); }, [mon, canView]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      await api.saveRecvPayOnly({
        recvId: Number(form.recvId), fromMon: form.fromMon || mon, toMon: form.toMon || null, reason: form.reason, allowBilled: form.allowBilled,
      });
      setForm({ recvId: '', fromMon: '', toMon: '', reason: '', allowBilled: false });
      await load(); onChanged?.();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const off = async (r: PayOnlyRow) => {
    if (!window.confirm(`접수 ${r.recv_id} ${r.cust_name ?? ''} 의 지급만 예외를 끌까요?\n그 달 청구가 없으면 기사 운행료가 다시 0원이 됩니다.`)) return;
    setBusy(true);
    try { await api.saveRecvPayOnly({ id: r.id, off: true }); await load(); onChanged?.(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const edit = (r: PayOnlyRow) => setForm({ recvId: String(r.recv_id), fromMon: r.from_mon, toMon: r.to_mon ?? '', reason: r.reason, allowBilled: r.allow_billed });

  const n = rows?.length ?? 0;
  if (!canView) return null;
  return (
    <>
      <button
        type="button"
        className="btn-ghost whitespace-nowrap text-xs"
        title="청구 없이 기사 지급만 하는 접수(관리자 지정) — 그 달 청구 줄이 하나도 없을 때만 명세에 운행료가 들어갑니다"
        onClick={() => setOpen(true)}
      >
        지급만 예외{n ? ` (${n})` : ''}
      </button>
      {open && (
        <Modal width="max-w-5xl">
          <div className="card space-y-3 p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">지급만(청구 없음) 예외 · {mon}</h2>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>닫기</button>
            </div>
            <p className="text-xs text-ink-soft">
              걸린 접수는 그 달 <b>청구 줄이 하나도 없으면</b>(노선 청구액·거래처 추가/차감) 청구 요청 없이도 기사 명세에 운행료·영업정산이 들어갑니다.
              청구 줄이 생기면 「청구 있음」 — 보통처럼 청구 요청을 거쳐야 합니다. 넣기·끄기는 관리자만 할 수 있습니다.
              <b>발행 대기</b>로 넣은 접수는 청구액이 있어도(세금계산서 발행 날짜가 아직 안 정해진 경우) 기사 명세에 운행료가 들어갑니다 — 청구는 나중에 청구 요청으로 하고, 그때까지 미청구 목록에 남습니다.
            </p>
            {err && <div className="text-sm text-red-600">{err}</div>}
            <div className="max-h-[50vh] overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-ink-soft">
                  <tr>
                    <th className="text-right">접수 NO</th><th className="text-left">거래처</th><th>구분</th><th>종류</th><th>기간</th>
                    <th className="text-left">사유</th><th className="text-right">이 달 청구</th><th className="text-right">이 달 운행료</th>
                    <th>이 달</th><th className="text-left">설정</th>{isAdmin && <th />}
                  </tr>
                </thead>
                <tbody>
                  {rows === null && <tr><td colSpan={11} className="py-3 text-center text-ink-faint">불러오는 중…</td></tr>}
                  {rows?.length === 0 && <tr><td colSpan={11} className="py-3 text-center text-ink-faint">지정한 접수가 없습니다.</td></tr>}
                  {rows?.map((r) => (
                    <tr key={r.id} className="border-t border-line">
                      <td className="text-right">{r.recv_id}</td>
                      <td>{r.cust_name ?? '—'}</td>
                      <td className="text-center">{r.recv_type}</td>
                      <td className={`whitespace-nowrap text-center ${r.allow_billed ? 'text-sky-700' : ''}`}
                        title={r.allow_billed ? '청구액이 있어도 지급 · 청구는 나중에 청구 요청으로' : '그 달 청구 줄이 하나도 없을 때만 지급'}>
                        {r.allow_billed ? '발행 대기' : '청구 없음'}
                      </td>
                      <td className="whitespace-nowrap text-center">{r.from_mon} ~ {r.to_mon ?? '계속'}</td>
                      <td className="max-w-[16rem] truncate" title={r.reason}>{r.reason}</td>
                      <td className="text-right">{money(r.cost_cust_total)}</td>
                      <td className="text-right">{money(r.cost_driver_total)}</td>
                      <td className={`whitespace-nowrap text-center ${r.state === '열림' ? 'text-emerald-700' : r.state === '청구 있음' ? 'text-amber-700' : 'text-ink-faint'}`}
                        title={r.state === '열림' ? (r.allow_billed ? '청구 요청 전이어도 명세에 운행료가 들어갑니다' : '청구 없이 명세에 운행료가 들어갑니다') : r.state === '청구 있음' ? '청구액이 있어 보통 경로(청구 요청)를 타야 합니다' : '이 달은 기간 밖입니다'}>
                        {r.state}
                      </td>
                      <td className="whitespace-nowrap text-xs text-ink-soft" title={`${r.created_by} ${r.created_at}`}>{(r.updated_by ?? r.created_by).split('@')[0]} {r.updated_at.slice(0, 10)}</td>
                      {isAdmin && (
                        <td className="whitespace-nowrap">
                          <button type="button" className="btn-ghost text-xs" onClick={() => edit(r)}>고치기</button>
                          <button type="button" className="btn-ghost text-xs text-red-600" disabled={busy} onClick={() => void off(r)}>끄기</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isAdmin && (
              <form
                className="flex flex-wrap items-end gap-2 border-t border-line pt-3"
                onSubmit={(e) => { e.preventDefault(); void save(); }}
              >
                <label className="text-xs">접수 NO
                  <input className="field w-24" inputMode="numeric" value={form.recvId} onChange={(e) => setForm({ ...form, recvId: e.target.value.replace(/[^0-9]/g, '') })} />
                </label>
                <label className="text-xs">시작월
                  <input className="field w-28" type="month" value={form.fromMon || mon} onChange={(e) => setForm({ ...form, fromMon: e.target.value })} />
                </label>
                <label className="text-xs">종료월(빈칸 = 계속)
                  <input className="field w-28" type="month" value={form.toMon} onChange={(e) => setForm({ ...form, toMon: e.target.value })} />
                </label>
                <label className="flex items-center gap-1 whitespace-nowrap pb-2 text-xs" title="청구액은 있는데 세금계산서 발행 날짜가 아직 안 정해진 접수 — 청구 요청 전에도 기사 지급">
                  <input type="checkbox" checked={form.allowBilled} onChange={(e) => setForm({ ...form, allowBilled: e.target.checked })} />
                  발행 대기(청구액 있어도 지급)
                </label>
                <label className="min-w-[16rem] flex-1 text-xs">사유(필수)
                  <input className="field w-full" maxLength={500} value={form.reason} placeholder="예: 다른 접수로 묶어 청구 · 회사 부담 운행" onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                </label>
                <button type="submit" className="btn-primary" disabled={busy || !form.recvId || !form.reason.trim()}>
                  넣기 / 고치기
                </button>
              </form>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
